import React, { useEffect, useMemo } from 'react';
import type { StationeryTemplate } from '../../../DesignsGallery.types';
import type { TagVariant } from '../../../../GalleryTile';
import { GalleryTile, GalleryTileWithRef } from '../../../../GalleryTile';
import { templateToTitle } from '@apps/card/utils/templateToTitle';
import { hasSuiteImage } from '@apps/card/utils/suiteImage';
import { maybeApplyCallback } from '@shared/utils/maybeApplyCallback';
import { Box } from '@withjoy/joykit';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { tileRowGap } from '../../CardsConfig';
import { ProductPrices } from './ProductPrices/ProductPrices';
import { useItemImpressionsTelemetry } from '../../ItemImpressionsTelemetry';
import { useInView } from '@shared/hooks/useInView';
import { GalleryTileCarousel } from './GalleryTileCarousel';
import { CardColorSwatches } from '@apps/card/components/CardColorSwatches';
import { useColorways } from '@apps/card/hooks/useColorways';
import { useGalleryColorwayOrder } from '../../../GalleryColorwayOrderContext';
import { useWebsiteThemeId } from '../../../WebsiteThemeIdContext';
import { useGallerySearchScore } from '../../../GallerySearchScoresContext';
import { SearchScoreDebug } from './SearchScoreDebug';
import type { PageJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { CardTileProps } from './CardTile.types';
import { useTileAltText } from './useTileAltText';

/** Mounted by CardTileShell once the tile is near the viewport. */
export const CardTile = (props: CardTileProps & Readonly<{ aspectRatio: string }>) => {
  const { isSuperAdmin } = useAuth();
  const { tq } = useTranslation('stationery');
  const { aspectRatio, cardTileSource, data, hideTag, preferredFormat } = props;
  const { category, approved, format, enabled } = data;

  const { buttonInteracted, markTemplateAsFavorite } = useCardTelemetry();

  // Keyed by template `id` to match `getGroupKey` in useSearchFilteredGalleryItems.
  const searchScore = useGallerySearchScore(data.id);

  const { goToProductDetailsPage, getProductDetailsPagePath } = useCardsRouterContext();
  const websiteThemeId = useWebsiteThemeId();

  const logTileClicked = (template: StationeryTemplate) =>
    buttonInteracted({
      name: 'designsGallery_tileClicked',
      themeId: template.themeId,
      stationeryTemplateCategory: template.category,
      format: template.format,
      isPremiumDesign: template.premium,
      hasSuiteImage: hasSuiteImage(template.themeId, selectedId),
    });

  const handleOnViewTemplatePdpClick = useEventCallback((template: StationeryTemplate) => {
    logTileClicked(template);
    goToProductDetailsPage('wedding', category, template.themeId, template.id, 'push', preferredFormat, selectedId ? selectedId : undefined);
  });

  const canViewPdp = !!(approved || isSuperAdmin);

  // The tile is a real <a href> so the browser handles modifier/middle clicks (new tab or
  // window) and crawlers see a link; a plain left-click stays an in-app navigation.
  const handleOnTileClick = useEventCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.button === 2) return; // context menu
    // Controls inside the tile (swatches, arrows, favorite) cancel their own `click`, but a
    // middle-click fires `auxclick`, which they don't handle — don't let it open the PDP.
    const button = e.target instanceof Element ? e.target.closest('button') : null;
    if (button && e.currentTarget.contains(button)) {
      e.preventDefault();
      return;
    }
    const isModifiedClick = e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
    if (isModifiedClick) {
      logTileClicked(data);
      return;
    }
    e.preventDefault();
    handleOnViewTemplatePdpClick(data);
  });

  const isPremium = data.premium || format === 'paper';

  const altText = useTileAltText(data, props.currentCategory);

  const tagVariant: TagVariant | undefined = enabled
    ? approved
      ? // No tag if enabled
        undefined
      : 'comingSoon'
    : 'disabled';

  // The shop gallery hides per-tile prices (the flagged-then-cemented treatment) and shows a
  // separate pricing showcase tile instead. The Favorites gallery has no such showcase, so it
  // keeps its per-tile price label.
  const showPrice = cardTileSource === 'favoritesGallery';
  const priceElement = showPrice ? <ProductPrices template={data} /> : undefined;

  const handleOnToggleFavorite = useEventCallback((isFavorite: boolean) => {
    markTemplateAsFavorite({ themeId: data.themeId, templateCategory: data.category, isFavorite, source: cardTileSource });
  });

  const { setImpression } = useItemImpressionsTelemetry();

  // Threshold set to 0.6 because:
  // a) When scrolling up, the image of the tile is fully revealed by 0.6. No need to wait until 1.0.
  // b) If 1.0, the right edge of tiles isn't counting as "in view". Perhaps we're off by a pixel in layout.
  const { ref: setInViewEl, inView } = useInView<HTMLDivElement>({ threshold: 0.6 });
  useEffect(() => {
    if (inView) setImpression(data.id);
  }, [data.id, setImpression, inView]);

  // While a search term, a `Colors` filter, or a single `Styles` filter is narrowing the gallery, order
  // swatches by how well each colorway matched (best first) and pre-select the
  // top one, so the thumbnail renders in it. Swatches are never hidden —
  // colorways that didn't rank keep their catalog order after the ranked ones.
  // Otherwise (or outside the designs gallery, e.g. favorites) the order is
  // undefined and the tile is untouched.
  const { colorwayIds: colorwayOrder, source: colorwayOrderSource } = useGalleryColorwayOrder(data.themeId);
  const pageFront: PageJson | undefined = data.themeJSON?.card?.front;
  const { sortedOptions, bestMatchId } = useMemo(() => {
    const allOptions = pageFront?.colorways?.options;
    if (!allOptions || !colorwayOrder?.length) return { sortedOptions: allOptions, bestMatchId: undefined };
    const rank = new Map(colorwayOrder.map((id, index) => [id, index]));
    // Stable sort: ranked colorways first (in match order), unranked keep their relative order at the end.
    const sorted = [...allOptions].sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity));
    const bestMatchId = colorwayOrder.find(id => allOptions.some(o => o.id === id));
    return { sortedOptions: sorted, bestMatchId };
  }, [pageFront, colorwayOrder]);

  // `bestMatchId` comes from an active search or filter curation; `data.colorwayOverride`
  // from the gallery-order flag repeating a template with a colorway. They never
  // coexist — the flag's expansion is skipped while any narrowing filter is active
  // (see `useDesignGalleryItems`) — so the order here is a formality.
  const colorwayOverride = bestMatchId ?? data.colorwayOverride;
  const { options, selectedId, setSelectedId, preloadImages } = useColorways(pageFront, 'small', colorwayOverride);

  // Digital-only designs use a Design Service `design.id`, not a stationery-template
  // id, so the favorite mutation can't persist them — hide the badge rather than show
  // a control that silently no-ops.
  const isDigitalOnly = format === 'digital';

  const renderedOptions = sortedOptions ?? options;

  const handleColorwaySelect = useEventCallback((id: string) => {
    buttonInteracted({
      name: 'colorwaySwatchClicked',
      source: cardTileSource,
      themeId: data.themeId,
      templateId: data.id,
      stationeryTemplateCategory: data.category,
      colorwayId: id,
      previousColorwayId: selectedId,
      swatchIndex: renderedOptions?.findIndex(option => option.id === id) ?? -1,
      totalColorways: renderedOptions?.length ?? 0,
      isSearchOrdered: colorwayOrderSource === 'search' && !!colorwayOrder?.length,
      colorwayOrderSource: colorwayOrder?.length ? colorwayOrderSource : undefined,
    });
    setSelectedId(id);
  });

  const handleColorwayOverflowClick = useEventCallback((hiddenCount: number) => {
    buttonInteracted({
      name: 'designsGallery_colorwayOverflowClicked',
      themeId: data.themeId,
      stationeryTemplateCategory: data.category,
      hiddenCount,
    });
    handleOnViewTemplatePdpClick(data);
  });

  const badges = (
    <>
      {isPremium && <GalleryTile.PremiumBadge />}
      {!isDigitalOnly && (
        <GalleryTile.FavoriteBadge
          eventId={props.eventId}
          onToggle={handleOnToggleFavorite}
          template={data}
        />
      )}
      {data.isFoil && <GalleryTile.FoilBadge />}
    </>
  );

  return (
    <GalleryTileWithRef
      ref={setInViewEl}
      aspectRatio={aspectRatio}
      alignContent="start"
      rowGap={tileRowGap}
      onMouseEnter={preloadImages}
      {...(canViewPdp
        ? {
            as: 'a' as const,
            href: getProductDetailsPagePath('wedding', category, data.themeId, data.id, preferredFormat, selectedId || undefined),
            onClick: handleOnTileClick,
            // Middle-click fires `auxclick`, not `click`; the browser opens the tab, we only log it.
            onAuxClick: handleOnTileClick,
            // Link semantics instead of `group`, but keep the marker `_groupHover` (CardThumbnail) keys off.
            role: undefined,
            'data-group': true,
            // The controls nested in the link would otherwise all be read as part of its name.
            'aria-label': templateToTitle(data),
            color: 'inherit',
            textDecoration: 'none',
          }
        : {})}
    >
      {websiteThemeId === data.themeId && (
        <Box
          typographyVariant="label1"
          fontWeight={600}
          position="absolute"
          paddingX={4}
          paddingY={2}
          left={5}
          top={4}
          backgroundColor="mono14"
          color="white"
          userSelect="none"
          borderRadius={5}
          zIndex={1}
        >
          {tq`Matches Your Site`}
        </Box>
      )}
      <GalleryTileCarousel
        template={data}
        selectedColorwayId={selectedId}
        fallbackImageUrl={isDigitalOnly ? data.digitalDesignThumbnailUrl : undefined}
        altText={altText}
      >
        {badges}
      </GalleryTileCarousel>

      <GalleryTile.Content
        title={templateToTitle(data)}
        belowTitle={
          <>
            {!!renderedOptions?.length && (
              <Box
                display="flex"
                justifyContent="center"
              >
                <CardColorSwatches
                  options={renderedOptions}
                  selectedId={selectedId}
                  onSelect={handleColorwaySelect}
                  variant="overflow"
                  onOverflowClick={maybeApplyCallback(canViewPdp, handleColorwayOverflowClick)}
                  showTooltip
                />
              </Box>
            )}
            {searchScore && <SearchScoreDebug score={searchScore} />}
          </>
        }
        label={priceElement}
      >
        {!hideTag && tagVariant && <GalleryTile.Tag variant={tagVariant} />}
      </GalleryTile.Content>
    </GalleryTileWithRef>
  );
};
